import { AfterViewInit, ChangeDetectionStrategy, Component, ElementRef, Input, NgZone, OnChanges, OnDestroy, inject } from '@angular/core';

/**
 * Estados de Kubo (mismos que la hoja de identidad del asistente):
 * reposo · saludo · pensando · explicando · exito · advertencia · pregunta ·
 * triste · carga.
 */
export type KuboMood = 'reposo' | 'saludo' | 'pensando' | 'explicando' | 'exito' | 'advertencia' | 'pregunta' | 'triste' | 'carga';

let nextAvatarId = 0;

/** Contorno del cuerpo: esfera con "capucha" que se curva y colita de globo de diálogo. */
const BODY = 'M57 13C81 10 102 24 107 47C111 66 104 83 90 92C80 99 68 101 58 100C52 104 45 109 36 111C41 105 43 100 40 95C24 86 15 70 16 53C18 30 35 15 57 13Z';
/** Visor tipo cúpula de vidrio. */
const VISOR = 'M29 58C29 40 43 30 61 30C80 30 94 40 94 56C94 72 81 82 61 82C43 82 29 73 29 58Z';

/**
 * Kubo dibujado en SVG (sin imágenes), con volumen: cuerpo blanco-azulado
 * con sombreado en capas, luz de borde y brillos; visor oscuro de vidrio con
 * ojos luminosos; anillo orbital y esfera azul con relieve. Cada estado
 * cambia la cara y el detalle de la esquina (!, ?, carga...).
 *
 * - `animated`: flota, parpadea y anima los detalles. Solo para usos FIJOS
 *   (dock, modales): dentro de contenido que se desplaza se deja estático
 *   para no crear capas que "se despeguen" al hacer scroll.
 * - Funciona en modo claro y oscuro (en oscuro gana un halo azul).
 */
@Component({
  selector: 'app-kubo-avatar',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <svg viewBox="0 0 128 128" class="kb" [class.kb-animated]="animated" [attr.data-mood]="mood" aria-hidden="true" focusable="false">
      <defs>
        <radialGradient [attr.id]="id + 'b'" cx="34%" cy="24%" r="88%">
          <stop offset="0" stop-color="#ffffff" />
          <stop offset=".32" stop-color="#f4f8ff" />
          <stop offset=".62" stop-color="#d3e3ff" />
          <stop offset=".84" stop-color="#9dbdfc" />
          <stop offset="1" stop-color="#5f8ff3" />
        </radialGradient>
        <linearGradient [attr.id]="id + 's'" x1="0" y1="0" x2="0" y2="1">
          <stop offset=".45" stop-color="#1d4ed8" stop-opacity="0" />
          <stop offset="1" stop-color="#1d4ed8" stop-opacity=".34" />
        </linearGradient>
        <linearGradient [attr.id]="id + 'l'" x1="0" y1="0" x2="1" y2=".3">
          <stop offset=".45" stop-color="#93c5fd" stop-opacity="0" />
          <stop offset="1" stop-color="#7cc4ff" stop-opacity=".95" />
        </linearGradient>
        <linearGradient [attr.id]="id + 'u'" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stop-color="#ffffff" stop-opacity=".95" />
          <stop offset=".55" stop-color="#e3eeff" stop-opacity=".75" />
          <stop offset="1" stop-color="#7aa7f7" stop-opacity=".7" />
        </linearGradient>
        <radialGradient [attr.id]="id + 'v'" cx="40%" cy="30%" r="78%">
          <stop offset="0" stop-color="#2b3a7c" />
          <stop offset=".45" stop-color="#121b4a" />
          <stop offset="1" stop-color="#050920" />
        </radialGradient>
        <linearGradient [attr.id]="id + 'r'" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0" stop-color="#1d4ed8" />
          <stop offset=".5" stop-color="#3b82f6" />
          <stop offset="1" stop-color="#93d2ff" />
        </linearGradient>
        <radialGradient [attr.id]="id + 'o'" cx="34%" cy="30%" r="74%">
          <stop offset="0" stop-color="#e0efff" />
          <stop offset=".3" stop-color="#7cb4ff" />
          <stop offset=".72" stop-color="#2563eb" />
          <stop offset="1" stop-color="#1e3a8a" />
        </radialGradient>
        <clipPath [attr.id]="id + 'c'"><path [attr.d]="body" /></clipPath>
        <clipPath [attr.id]="id + 'q'"><path [attr.d]="visor" /></clipPath>
        <filter [attr.id]="id + 'g'" filterUnits="userSpaceOnUse" x="0" y="0" width="128" height="128">
          <feGaussianBlur stdDeviation="2.2" result="blur" />
          <feMerge><feMergeNode in="blur" /><feMergeNode in="blur" /><feMergeNode in="SourceGraphic" /></feMerge>
        </filter>
        <filter [attr.id]="id + 'h'" filterUnits="userSpaceOnUse" x="0" y="0" width="128" height="128"><feGaussianBlur stdDeviation="2.4" /></filter>
        <filter [attr.id]="id + 'k'" filterUnits="userSpaceOnUse" x="-20" y="-20" width="168" height="168"><feGaussianBlur stdDeviation="4.5" /></filter>
      </defs>

      @if (shadow) {
        <ellipse class="kb-shadow" cx="62" cy="120" rx="30" ry="4.5" [attr.filter]="'url(#' + id + 'h)'" />
      }

      <g class="kb-float">
        <!-- Mitad trasera del anillo -->
        <g transform="rotate(-10 62 70)"><g class="kb-halo">
          <path class="kb-ring back" d="M5 70A57 15.5 0 0 1 119 70" [attr.stroke]="'url(#' + id + 'r)'" />
        </g></g>

        <!-- Cuerpo con volumen -->
        <path [attr.d]="body" [attr.fill]="'url(#' + id + 'b)'" />
        <g [attr.clip-path]="'url(#' + id + 'c)'">
          <path [attr.d]="body" [attr.fill]="'url(#' + id + 's)'" />
          <!-- Resplandor interior azul (parte baja) -->
          <ellipse cx="68" cy="99" rx="40" ry="15" fill="#2f7cff" opacity=".55" [attr.filter]="'url(#' + id + 'k)'" />
          <!-- Luz de borde a la derecha -->
          <path [attr.d]="body" fill="none" [attr.stroke]="'url(#' + id + 'l)'" stroke-width="7" [attr.filter]="'url(#' + id + 'h)'" />
          <!-- Capucha en remolino: sombra bajo el pliegue y el pliegue en sí -->
          <path d="M101 88C107 62 99 35 71 26.5C57 22.5 42 22.5 26 24" fill="none" stroke="#1d4ed8" stroke-opacity=".32" stroke-width="6" [attr.filter]="'url(#' + id + 'h)'" />
          <path d="M26 24C40 15 50 13 57 13C81 10 102 24 107 47C110 62 107 76 100 86C106 61 98 34 70 25C56 21 42 21 26 23Z" [attr.fill]="'url(#' + id + 'u)'" />
          <path d="M100 86C106 61 98 34 70 25C56 21 42 21 26 23" fill="none" stroke="#fff" stroke-opacity=".9" stroke-width="1.3" />
          <!-- Sombra que proyecta el visor -->
          <path [attr.d]="visor" fill="#173a8f" opacity=".35" transform="translate(2.5 4)" [attr.filter]="'url(#' + id + 'h)'" />
        </g>
        <!-- Brillo especular principal -->
        <ellipse cx="40" cy="27" rx="13" ry="6" fill="#fff" opacity=".95" transform="rotate(-28 40 27)" [attr.filter]="'url(#' + id + 'h)'" />
        <ellipse cx="38" cy="26" rx="6" ry="2.4" fill="#fff" transform="rotate(-28 38 26)" />

        <!-- Visor de vidrio -->
        <path [attr.d]="visor" [attr.fill]="'url(#' + id + 'v)'" />
        <g [attr.clip-path]="'url(#' + id + 'q)'">
          <path d="M26 86C40 76 82 76 98 86" fill="none" stroke="#3b82f6" stroke-opacity=".55" stroke-width="5" [attr.filter]="'url(#' + id + 'h)'" />
          <path d="M37 41C47 33 66 31 80 35" fill="none" stroke="#fff" stroke-opacity=".3" stroke-width="5" stroke-linecap="round" [attr.filter]="'url(#' + id + 'h)'" />
        </g>
        <path [attr.d]="visor" fill="none" stroke="#0a1238" stroke-width="1.4" />
        <path d="M40 38C48 34 58 33 66 33.5" fill="none" stroke="#fff" stroke-opacity=".55" stroke-width="1.6" stroke-linecap="round" />

        <!-- Cara según el estado. Dos capas de movimiento: .kb-look sigue al
             cursor (variables --kb-lx/--kb-ly) y .kb-glance mira a los lados
             de vez en cuando cuando nadie mueve el cursor. -->
        <g class="kb-look"><g class="kb-glance">
        <g class="kb-face" [attr.filter]="'url(#' + id + 'g)'" fill="#68d0ff" stroke="#68d0ff" stroke-linecap="round" stroke-linejoin="round">
          @switch (mood) {
            @case ('saludo') {
              <g class="kb-eyes"><ellipse cx="49" cy="56" rx="4.8" ry="7" stroke="none" /><ellipse cx="73" cy="55" rx="4.8" ry="7" stroke="none" /></g>
            }
            @case ('pensando') {
              <g class="kb-eyes"><circle cx="47" cy="53" r="3.6" stroke="none" /><circle cx="70" cy="52" r="3.6" stroke="none" /></g>
              <path d="M56 67Q59 64.5 62 67T68 67" fill="none" stroke-width="2.4" />
            }
            @case ('advertencia') {
              <g class="kb-eyes"><rect x="44.5" y="48" width="8" height="15" rx="4" stroke="none" /><rect x="68.5" y="47" width="8" height="15" rx="4" stroke="none" /></g>
            }
            @case ('pregunta') {
              <g class="kb-eyes"><ellipse cx="49" cy="55" rx="4.4" ry="5.8" stroke="none" /><ellipse cx="73" cy="54" rx="4.4" ry="5.8" stroke="none" /></g>
              <path d="M56 66.5Q61 71 66 66.5" fill="none" stroke-width="2.6" />
            }
            @case ('triste') {
              <path d="M42 56Q49 61.5 56 56M66 55Q73 60.5 80 55" fill="none" stroke-width="4" />
              <path d="M56.5 70.5Q61 67 65.5 70.5" fill="none" stroke-width="2.4" />
            }
            @case ('carga') {
              <circle class="kb-load" cx="61" cy="56" r="11" fill="none" stroke-width="3.6" />
            }
            @default {
              <g class="kb-eyes"><path d="M42 60Q49 48.5 56 60M66 59Q73 47.5 80 59" fill="none" stroke-width="5" /></g>
            }
          }
        </g>
        </g></g>
        @if (mood === 'triste') {
          <path d="M86 70c0 0-3.4 4.2-3.4 6.3a3.4 3.4 0 0 0 6.8 0C89.4 74.2 86 70 86 70Z" fill="#9ad8ff" opacity=".9" />
        }

        <!-- Mitad delantera del anillo -->
        <g transform="rotate(-10 62 70)"><g class="kb-halo">
          <path class="kb-ring glow" d="M5 70A57 15.5 0 0 0 119 70" [attr.stroke]="'url(#' + id + 'r)'" [attr.filter]="'url(#' + id + 'h)'" />
          <path class="kb-ring" d="M5 70A57 15.5 0 0 0 119 70" [attr.stroke]="'url(#' + id + 'r)'" />
          <path d="M30 80.5A57 15.5 0 0 0 80 85" fill="none" stroke="#fff" stroke-opacity=".55" stroke-width="1" stroke-linecap="round" />
          @if (animated) { <path class="kb-ring-shine" d="M5 70A57 15.5 0 0 0 119 70" fill="none" stroke="#fff" stroke-width="2.2" stroke-linecap="round" /> }
        </g></g>

        <!-- Detalle superior según el estado -->
        @switch (mood) {
          @case ('advertencia') {
            <g class="kb-badge">
              <circle cx="106" cy="20" r="10.5" fill="#ef4444" />
              <ellipse cx="103" cy="15.5" rx="5" ry="2.4" fill="#fff" opacity=".35" />
              <path d="M106 14v7.6" stroke="#fff" stroke-width="3.2" stroke-linecap="round" />
              <circle cx="106" cy="26.4" r="1.9" fill="#fff" />
            </g>
          }
          @case ('pregunta') {
            <g class="kb-badge">
              <path d="M99 12.5a7.5 7.5 0 1 1 10.5 7c-2.4 1.2-3.5 2.6-3.5 5.5" fill="none" stroke="#2563eb" stroke-width="4.4" stroke-linecap="round" />
              <circle cx="106" cy="31" r="2.6" fill="#2563eb" />
            </g>
          }
          @case ('carga') {
            <circle class="kb-load-mini" cx="106" cy="18" r="7" fill="none" stroke="#7cb4ff" stroke-width="2.4" stroke-dasharray="2.2 3.3" stroke-linecap="round" />
          }
          @default {
            <g class="kb-orb">
              <circle cx="105" cy="19" r="8.5" fill="#1d4ed8" opacity=".3" [attr.filter]="'url(#' + id + 'h)'" />
              <circle cx="104" cy="18" r="8" [attr.fill]="'url(#' + id + 'o)'" [attr.opacity]="mood === 'triste' ? .6 : 1" />
              <ellipse cx="101.5" cy="15" rx="3" ry="1.8" fill="#fff" opacity=".85" />
            </g>
          }
        }
        @if (mood === 'pensando') {
          <g class="kb-dots" fill="#3b82f6"><circle cx="86" cy="22" r="1.8" /><circle cx="92" cy="17" r="2.2" /><circle cx="80" cy="16" r="1.4" /></g>
        }

        <!-- Trazos de energía (saludo, explicando, éxito) -->
        @if (mood === 'saludo' || mood === 'explicando' || mood === 'exito' || mood === 'reposo') {
          <g class="kb-spark" fill="none" [attr.stroke]="mood === 'exito' ? '#22c55e' : '#3b82f6'" stroke-width="2.6" stroke-linecap="round">
            <path d="M17 26l-3.5-5.5" /><path d="M11 36l-6-1.5" />
            @if (mood !== 'reposo') { <path d="M24 18l-1.4-5.6" /> }
          </g>
        }
      </g>
    </svg>
  `,
  styles: [`
    :host { display: inline-block; width: 64px; height: 64px; flex: none; line-height: 0; }
    .kb { width: 100%; height: 100%; overflow: visible; }
    .kb-ring { fill: none; stroke-width: 3.6; stroke-linecap: round; }
    .kb-ring.back { stroke-width: 3; opacity: .45; }
    .kb-ring.glow { stroke-width: 5; opacity: .7; }
    .kb-shadow { fill: rgba(37, 99, 235, .3); }
    .kb-load { stroke-dasharray: 5 4.2; }
    :host-context(html.tusala-dark-theme) .kb { filter: drop-shadow(0 0 8px rgba(47, 124, 255, .45)); }
    :host-context(html.tusala-dark-theme) .kb-shadow { fill: rgba(59, 130, 246, .45); }

    /* Animaciones (solo con [animated]) */
    .kb-float { transform-box: view-box; transform-origin: 62px 100px; }
    .kb-animated .kb-float { animation: kbFloat 4.2s ease-in-out infinite; }
    .kb-animated .kb-eyes { transform-box: fill-box; transform-origin: center; animation: kbBlink 5.2s ease-in-out infinite; }
    .kb-animated .kb-orb { animation: kbOrb 3.4s ease-in-out infinite; }
    .kb-animated .kb-spark path { animation: kbSpark 2.6s ease-in-out infinite; }
    .kb-animated .kb-spark path:nth-child(2) { animation-delay: -.9s; }
    .kb-animated .kb-spark path:nth-child(3) { animation-delay: -1.7s; }
    .kb-animated .kb-dots circle { animation: kbDot 1.2s ease-in-out infinite; }
    .kb-animated .kb-dots circle:nth-child(2) { animation-delay: .2s; }
    .kb-animated .kb-dots circle:nth-child(3) { animation-delay: .4s; }
    .kb-animated .kb-load, .kb-animated .kb-load-mini { transform-box: fill-box; transform-origin: center; animation: kbSpin 1.1s linear infinite; }
    .kb-animated .kb-badge { transform-box: fill-box; transform-origin: center; animation: kbPop .4s cubic-bezier(.2, .9, .3, 1.4) both; }
    .kb-animated .kb-face { animation: kbFace .22s ease both; }
    /* Mirada: sigue al cursor con suavidad; si nadie lo mueve, vistazos ocasionales. */
    .kb-look { transform: translate(var(--kb-lx, 0px), var(--kb-ly, 0px)); transition: transform .45s cubic-bezier(.2, .8, .2, 1); }
    .kb-animated .kb-glance { animation: kbGlance 13s ease-in-out infinite; animation-delay: var(--kb-glance-delay, 0s); }
    .kb-tracking .kb-glance { animation: none; }
    /* Halo: se mece apenas y un brillo lo recorre. */
    .kb-halo { transform-box: view-box; transform-origin: 62px 70px; }
    .kb-animated .kb-halo { animation: kbHalo 7.5s ease-in-out infinite; }
    .kb-ring-shine { opacity: 0; stroke-dasharray: 16 240; stroke-dashoffset: 24; }
    .kb-animated .kb-ring-shine { animation: kbShine 5.5s ease-in-out infinite; }
    [data-mood='carga'] .kb-ring-shine { display: none; }
    .kb-animated[data-mood='saludo'] .kb-float { animation: kbHello 1.6s ease-in-out 2, kbFloat 4.2s 3.2s ease-in-out infinite; }
    .kb-animated[data-mood='exito'] .kb-float { animation: kbJump .55s ease-out 2, kbFloat 4.2s 1.1s ease-in-out infinite; }

    @keyframes kbFloat { 0%, 100% { transform: translateY(0); } 50% { transform: translateY(-3px); } }
    @keyframes kbHello { 0%, 100% { transform: rotate(0); } 25% { transform: rotate(-5deg); } 60% { transform: rotate(4deg); } }
    @keyframes kbJump { 0%, 100% { transform: translateY(0); } 45% { transform: translateY(-7px); } }
    @keyframes kbBlink { 0%, 93%, 100% { transform: scaleY(1); } 96% { transform: scaleY(.12); } }
    @keyframes kbOrb { 0%, 100% { transform: translateY(0); } 50% { transform: translateY(-3px); } }
    @keyframes kbSpark { 0%, 100% { opacity: 1; } 50% { opacity: .25; } }
    @keyframes kbDot { 0%, 100% { opacity: .3; } 50% { opacity: 1; } }
    @keyframes kbSpin { to { transform: rotate(360deg); } }
    @keyframes kbPop { from { opacity: 0; transform: scale(.4); } }
    @keyframes kbFace { from { opacity: 0; } }
    @keyframes kbGlance {
      0%, 14%, 36%, 52%, 74%, 100% { transform: translate(0, 0); }
      18%, 31% { transform: translate(-4px, .5px); }
      56%, 69% { transform: translate(4px, -2px); }
      79%, 86% { transform: translate(-1.5px, 2px); }
    }
    @keyframes kbHalo { 0%, 100% { transform: rotate(0deg) scale(1); } 35% { transform: rotate(3.5deg) scale(1.015); } 70% { transform: rotate(-2.5deg) scale(.99); } }
    @keyframes kbShine { 0% { stroke-dashoffset: 24; opacity: 0; } 12% { opacity: .85; } 55% { opacity: .85; } 68%, 100% { stroke-dashoffset: -150; opacity: 0; } }

    /* Mientras la página se desplaza no se anima nada (ver scroll-hover-guard). */
    :host-context(html.is-scrolling) .kb * { animation-play-state: paused !important; }
    @media (prefers-reduced-motion: reduce) { .kb * { animation: none !important; } }
  `],
})
export class KuboAvatarComponent implements AfterViewInit, OnChanges, OnDestroy {
  @Input() mood: KuboMood = 'reposo';
  @Input() animated = false;
  @Input() shadow = false;
  /** Prefijo único: los degradados de varios Kubo en pantalla no se pisan. */
  readonly id = `kubo-${++nextAvatarId}-`;
  readonly body = BODY;
  readonly visor = VISOR;

  private readonly host = inject(ElementRef<HTMLElement>);
  private readonly zone = inject(NgZone);
  private listening = false;
  private frame = 0;
  private idleTimer?: ReturnType<typeof setTimeout>;
  private lastPointer: { x: number; y: number } | null = null;

  constructor() {
    // Cada Kubo mira a los lados en su propio momento (no todos a la vez).
    (this.host.nativeElement as HTMLElement).style.setProperty('--kb-glance-delay', `-${(nextAvatarId * 3.7) % 13}s`);
  }

  ngAfterViewInit(): void { this.syncPointer(); }
  ngOnChanges(): void { this.syncPointer(); }

  ngOnDestroy(): void {
    this.stopPointer();
  }

  /** Solo los Kubo animados (dock, modales) siguen el cursor; los estáticos no escuchan nada. */
  private syncPointer(): void {
    const reduce = typeof matchMedia === 'function' && matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (this.animated && !reduce && !this.listening) {
      this.listening = true;
      this.zone.runOutsideAngular(() => document.addEventListener('pointermove', this.onPointer, { passive: true }));
    } else if ((!this.animated || reduce) && this.listening) {
      this.stopPointer();
    }
  }

  private stopPointer(): void {
    if (this.listening) document.removeEventListener('pointermove', this.onPointer);
    this.listening = false;
    if (this.frame) cancelAnimationFrame(this.frame);
    if (this.idleTimer) clearTimeout(this.idleTimer);
    this.frame = 0;
  }

  private onPointer = (event: PointerEvent): void => {
    if (event.pointerType === 'touch') return;
    this.lastPointer = { x: event.clientX, y: event.clientY };
    if (!this.frame) this.frame = requestAnimationFrame(() => { this.frame = 0; this.look(); });
  };

  /** Mueve los ojos hacia el cursor (como mucho unos px dentro del visor). */
  private look(): void {
    const el = this.host.nativeElement as HTMLElement;
    const svg = el.firstElementChild as SVGElement | null;
    const p = this.lastPointer;
    if (!svg || !p) return;
    const r = el.getBoundingClientRect();
    if (!r.width) return;
    const dx = p.x - (r.left + r.width / 2), dy = p.y - (r.top + r.height / 2);
    const range = 520;
    const clamp = (v: number) => Math.max(-1, Math.min(1, v));
    el.style.setProperty('--kb-lx', `${(clamp(dx / range) * 4.5).toFixed(2)}px`);
    el.style.setProperty('--kb-ly', `${(clamp(dy / range) * 2.6).toFixed(2)}px`);
    svg.classList.add('kb-tracking');
    if (this.idleTimer) clearTimeout(this.idleTimer);
    // Sin movimiento por un rato: vuelve al centro y retoma los vistazos.
    this.idleTimer = setTimeout(() => {
      el.style.setProperty('--kb-lx', '0px');
      el.style.setProperty('--kb-ly', '0px');
      svg.classList.remove('kb-tracking');
    }, 2600);
  }
}
